import React, { useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import cs from 'classnames';
import { useRecoilValue } from 'recoil';

import { deleteCustomFormType } from 'api/CustomForms';

import { iaRa, optionify } from 'lib/helpers/utility';
import { useCustomFormTypes } from 'lib/hooks/queries/customForms/useCustomFormTypes';

import practiceState from 'components/practice/practiceState';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Select from 'components/shared/Select/Select';

import CreateFormFunction from './components/CreateFormFunction';
import { formatCreateLabel, formatOption } from './components/SelectCustomParts';
import { DEFAULT_CREATE_MODAL_PROPS } from './lib/constants';
import { handleDeleteCustomType } from './lib/handleDeleteCustomType';

const CustomFormTypeSelect = ({
  formik,
  currentForm,
  onSelectChange = () => {},
  className,
  ...rest
}) => {
  const { practice: outletPractice } = useOutletContext() || {};
  const [selectedForm, setSelectedForm] = useState(currentForm ?? null);
  const [createModalProps, setCreateModalProps] = useState(DEFAULT_CREATE_MODAL_PROPS);
  const [isConfirmModal, setIsConfirmModal] = useState(false);
  const [currentFormId, setCurrentFormId] = useState(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectRef = useRef(null);

  const recoilPractice = useRecoilValue(practiceState.currentPractice);
  const practice = outletPractice || recoilPractice;

  const openConfirmModal = () => setIsConfirmModal(true);
  const closeIsConfirmModal = ({ clear = false, resetFocus = false } = {}) => {
    setIsConfirmModal(false);

    if (resetFocus) {
      setTimeout(() => {
        selectRef?.current?.blur();
      }, 100);
    }

    if (clear) {
      setCurrentFormId(null);
      handleOnChange(null);
    }
  };

  const { data: customFormTypes, isLoading } = useCustomFormTypes({
    params: { practice_id: practice?.id },
    dependencies: [practice?.id],
    options: {
      enabled: !!practice?.id,
      select: (data) => optionify(data?.form_functions, 'name', 'id', (e) => e)
    }
  });

  const { mutateAsync: mutateDeleteCustomType, isLoading: isDeleting } = useMutation({
    mutationFn: (data) => deleteCustomFormType(navigate, data)
  });

  const handleOnChange = (form) => {
    setSelectedForm(form);
    onSelectChange(form);
  };

  const handleRemoveOption = ({ event, id }) => {
    if (!event || !id) return;

    event.stopPropagation();

    openConfirmModal();

    setCurrentFormId(id);
  };

  const handleContinue = async () => {
    await mutateDeleteCustomType(
      { formId: currentFormId },
      {
        onSettled: (data) =>
          handleDeleteCustomType({
            data,
            currentFormId,
            customFormTypes,
            queryClient,
            selectedForm,
            closeIsConfirmModal,
            handleOnChange
          })
      }
    );
  };

  const onCreateOption = (inputValue) => {
    const existingOption = iaRa(customFormTypes).find((option) => option?.value === inputValue);

    if (existingOption) {
      return;
    }

    const passIfInputValueIsString = typeof inputValue === 'string' ? inputValue : null;
    setCreateModalProps({ open: true, name: passIfInputValueIsString });
    selectRef?.current?.blur();
  };

  return (
    <div className="w-[230px]">
      <Select
        parentClassName={cs('w-full', className)}
        forwardedRef={selectRef}
        isCreatable
        rightIcon="add-linear"
        rightIconColor="neutral"
        rightIconShade={400}
        rightIconSize={20}
        rightIconClick={onCreateOption}
        placeholder="Search forms"
        inputId="form-select"
        isClearable={false}
        isLoading={isLoading || isDeleting}
        options={iaRa(customFormTypes)}
        value={selectedForm}
        onChange={(v) => handleOnChange(v)}
        formatOptionLabel={(props) => formatOption({ props, handleRemoveOption })}
        formatCreateLabel={(inputValue) => formatCreateLabel({ inputValue, setCreateModalProps })}
        onCreateOption={onCreateOption}
        error={formik?.errors?.type}
        {...rest}
      />
      {createModalProps?.open && (
        <CreateFormFunction
          modalProps={createModalProps}
          hideModal={() => setCreateModalProps(DEFAULT_CREATE_MODAL_PROPS)}
          setSelectedForm={handleOnChange}
        />
      )}
      <Confirm
        handleOpen={isConfirmModal}
        handleClose={closeIsConfirmModal}
        icon="trash-mono"
        title="Delete Form Type?"
        message={
          <p>
            Are you sure you want to delete this form type?{' '}
            <em className="text-danger-600">
              If any forms exist under this type, they will be moved to the HP (Advanced History and
              Examination) section.
            </em>
          </p>
        }
        variant="danger"
        footerActions={{
          primary: {
            onClick: handleContinue,
            color: 'danger',
            isLoading: isDeleting,
            isDisabled: isDeleting
          }
        }}
      />
    </div>
  );
};

export default withErrorBoundary(CustomFormTypeSelect);
